/**
 * Curated, white-labeled email-client catalogue for the preview feature.
 *
 * Mailgun exposes ~100 raw client ids (e.g. `android16_gmailapp_pixel10_dm`).
 * We surface a small, human-labeled subset so customers and the agent pick a
 * "specific inbox / device" without ever seeing the underlying provider ids.
 * Every id below was validated against the live `GET /v1/preview/tests/clients`
 * catalogue. Keep this list current — a stale id renders nothing (Mailgun warns
 * and skips it), so `resolvePreviewClients` rejects unknown ids up front.
 */

type PreviewClientCategory = 'gmail' | 'outlook' | 'apple' | 'yahoo' | 'other'

export type PreviewClient = {
  /** The Mailgun client id sent to the Inspect API. */
  readonly id: string
  /** White-labeled human label shown to customers + the agent. */
  readonly label: string
  /** Provider family, for grouping in a UI. */
  readonly category: PreviewClientCategory
  /** Human OS / surface (e.g. "macOS", "iOS", "Windows", "Web"). */
  readonly os: string
  /** Whether this is the dark-mode variant. */
  readonly dark: boolean
}

export const PREVIEW_CLIENTS: ReadonlyArray<PreviewClient> = [
  // Gmail
  {
    id: 'gmailcom-lm_chrcurrent_win10',
    label: 'Gmail (Web)',
    category: 'gmail',
    os: 'Web',
    dark: false,
  },
  {
    id: 'gmailcom-dm_chrcurrent_win10',
    label: 'Gmail (Web, Dark)',
    category: 'gmail',
    os: 'Web',
    dark: true,
  },
  {
    id: 'android16_gmailapp_pixel10_lm',
    label: 'Gmail (Android)',
    category: 'gmail',
    os: 'Android',
    dark: false,
  },
  {
    id: 'android16_gmailapp_pixel10_dm',
    label: 'Gmail (Android, Dark)',
    category: 'gmail',
    os: 'Android',
    dark: true,
  },
  {
    id: 'iphone16gmail_18',
    label: 'Gmail (iOS)',
    category: 'gmail',
    os: 'iOS',
    dark: false,
  },
  // Outlook
  {
    id: 'outlook2021_win11_lm_dt',
    label: 'Outlook 2021 (Windows)',
    category: 'outlook',
    os: 'Windows',
    dark: false,
  },
  {
    id: 'outlook2021_win11_dm_dt',
    label: 'Outlook 2021 (Windows, Dark)',
    category: 'outlook',
    os: 'Windows',
    dark: true,
  },
  {
    id: 'o365_w10_lm_dt',
    label: 'Outlook 365 (Windows)',
    category: 'outlook',
    os: 'Windows',
    dark: false,
  },
  {
    id: 'outlookcom-lm_chrcurrent_win10',
    label: 'Outlook.com (Web)',
    category: 'outlook',
    os: 'Web',
    dark: false,
  },
  // Apple
  {
    id: 'applemail16',
    label: 'Apple Mail (macOS)',
    category: 'apple',
    os: 'macOS',
    dark: false,
  },
  {
    id: 'applemail16_dm',
    label: 'Apple Mail (macOS, Dark)',
    category: 'apple',
    os: 'macOS',
    dark: true,
  },
  {
    id: 'iphone16_18',
    label: 'Apple Mail (iOS)',
    category: 'apple',
    os: 'iOS',
    dark: false,
  },
  {
    id: 'iphone16_18_dm',
    label: 'Apple Mail (iOS, Dark)',
    category: 'apple',
    os: 'iOS',
    dark: true,
  },
  // Yahoo
  {
    id: 'yahoocom-lm_chrcurrent_win10',
    label: 'Yahoo Mail (Web)',
    category: 'yahoo',
    os: 'Web',
    dark: false,
  },
]

export const PREVIEW_CLIENT_BY_ID: ReadonlyMap<string, PreviewClient> = new Map(
  PREVIEW_CLIENTS.map((c) => [c.id, c])
)

/**
 * Default set when the caller does not specify clients: a small, FAST,
 * representative spread (web + mobile + macOS across the big four) that
 * comfortably renders inside the bounded blocking window. Deliberately excludes
 * the slow Outlook desktop (Word engine) renders — request those explicitly.
 */
export const DEFAULT_PREVIEW_CLIENT_IDS: ReadonlyArray<string> = [
  'gmailcom-lm_chrcurrent_win10',
  'android16_gmailapp_pixel10_lm',
  'applemail16',
  'iphone16_18',
  'outlookcom-lm_chrcurrent_win10',
]

/** Every valid client id the caller may request. */
export function validPreviewClientIds(): Array<string> {
  return PREVIEW_CLIENTS.map((c) => c.id)
}

/**
 * Compact `id = label` catalogue listing for schema/tool descriptions — the
 * ONE string that teaches a caller (API consumer, MCP client, or the
 * orchestrator agent) how to map "X client on X device" to a client id.
 * Derived from {@link PREVIEW_CLIENTS} so it can never drift from the catalog.
 */
export const PREVIEW_CLIENT_CATALOG_TEXT: string = PREVIEW_CLIENTS.map(
  (c) => `${c.id} = ${c.label}`
).join('; ')

export type ResolvePreviewClientsResult = {
  clients: Array<PreviewClient>
  invalid: Array<string>
}

/**
 * Map caller-requested ids → catalogue entries, de-duped and order-preserving.
 * Omitted / empty input falls back to {@link DEFAULT_PREVIEW_CLIENT_IDS}.
 * Unknown ids are returned in `invalid` so the caller can 400 with a helpful
 * message listing the supported ids.
 */
export function resolvePreviewClients(
  requested?: ReadonlyArray<string>
): ResolvePreviewClientsResult {
  const ids =
    requested && requested.length > 0 ? requested : DEFAULT_PREVIEW_CLIENT_IDS
  const seen = new Set<string>()
  const clients: Array<PreviewClient> = []
  const invalid: Array<string> = []
  for (const id of ids) {
    if (seen.has(id)) {
      continue
    }
    seen.add(id)
    const client = PREVIEW_CLIENT_BY_ID.get(id)
    if (client) {
      clients.push(client)
    } else {
      invalid.push(id)
    }
  }
  return { clients, invalid }
}
